import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { BrowserAppTemplate } from '../BrowserAppTemplate';
import { getNodeById, NodeType } from '../../shared/cache/NodeCache';
import { LoadingScreen } from '../../shared/components/LoadingScreen';
import { AccessDenied } from '../../shared/components/AccessDenied';

interface NestedComponentProperties {
  node: number,
  type: NodeType,
  data?: any
}

export const NestedComponent: React.FC<NestedComponentProperties> = ({ node, type, data }) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [isAuthorizedAccess, setIsAuthorizedAccess] = useState<boolean>(true);
  const [url, setUrl] = useState<string>('');
  
  const parseUrl = async () => {
    const n = await getNodeById(type, node);

    // The node list is scoped server side, so a node we cannot find is either one
    // this user was never given or one that no longer exists. Both end the same
    // way: nothing to load, and the same denial a restricted ticket shows. #35097
    if (!n) {
      setIsAuthorizedAccess(false);
      setLoading(false);
      return;
    }

    setUrl(n.Url.replace(/\{(\w+)\}/g, (text, key) => data?.[key] ?? text));
    setLoading(false);
  };

  useEffect(() => { parseUrl(); }, [node]);

  if (loading) return <LoadingScreen message={''} />;
  if (!isAuthorizedAccess) return <AccessDenied />;

  return <iframe width="100%" height="100%" src={url} allow="geolocation; microphone; camera"/>
}


interface NestedPageProperties {
  data?: any
}

export const NestedPage: React.FC<NestedPageProperties> = ({ data }) => {
  const { t } = useTranslation();
  const { nodeId } = useParams<any>();
  const [nodeName, setNodeName] = useState<string>('');

  // The title is set here rather than in NestedComponent, which also renders custom tabs inside a
  // ticket, where the ticket's own title has to win.
  useEffect(() => {
    let cancelled = false;
    getNodeById(NodeType.Page, nodeId).then(n => { if (!cancelled) setNodeName(n?.Name ?? ''); });
    return () => { cancelled = true; };
  }, [nodeId]);

  return (
    <>
      <Helmet>
        <title>{nodeName ? `${nodeName} - ${t('tab-title.postfix')}` : t('tab-title.postfix')}</title>
      </Helmet>
      <BrowserAppTemplate page={<NestedComponent node={nodeId} type={NodeType.Page} data={data} />} active={`node-${nodeId}`} hideSettingsNav={true} />
    </>
  );
}
